import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
// @mui
import {
  Stack,
  Button,
  Skeleton,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { CityReqTable } from '../sections/@dashboard/interfaces';
import SearchComponet from '../components/search/Search';
import ModalComponent from '../components/modal/modal';
import InterfaceForm from '../sections/@dashboard/interfaces/InterfaceForm';
import ConfirmationModal from '../components/confirmationModal/ConfirmationModal';
import { getInterfacesList, deleteInterface } from '../network/apis';
import { ReactComponent as DropdownIcon } from '../theme/images/deopdownIcon.svg';

export default function Interfaces() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientReqList, setClientReqList] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  // delete modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletedId, setDeletedId] = useState(null);

  // add/edit modal state
  const [isModalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [editData, setEditData] = useState({});

  const categoryOptions = [
    { id: 1, labelAr: 'سكني', labelEn: 'Residential', value: 'RESIDENTIAL' },
    { id: 2, labelAr: 'تجاري', labelEn: 'Commercial', value: 'COMMERCIAL' },
  ];

  const clientRequests = (pageNum = page, searchVal = search, categoryVal = category) => {
    setLoading(true);
    getInterfacesList(pageNum, searchVal, categoryVal)
      .then((res) => {
        setClientReqList(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    clientRequests(page, search, category);
  }, [page, search, category]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDelete = (id) => {
    deleteInterface(id)
      .then((res) => {
        if (res?.data?.STATUS === 'FAILED' || res?.data?.STATUS === '0') {
          toast.error(res?.data?.MESSAGE || res?.data?.message);
        } else {
          toast.success(res?.data?.MESSAGE || res?.data?.message);
          clientRequests(page, search, category);
        }
        setOpenDeleteModal(false);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.MESSAGE || error?.response?.data?.message || error?.message);
        setOpenDeleteModal(false);
      });
  };

  const handleOpenAddModal = () => {
    setEditData({});
    setMode('add');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditData({});
    clientRequests(page, search, category);
  };

  return (
    <>
      <Helmet>
        <title> {t('interface.interface')} </title>
      </Helmet>

      {/* Add / Edit Modal */}
      <ModalComponent
        style={{ position: 'relative' }}
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        title={mode === 'edit' ? t('interface.editInterface') : t('interface.addInterface')}
        content=""
      >
        <InterfaceForm handleClose={handleCloseModal} mode={mode} data={editData} />
      </ModalComponent>

      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <ConfirmationModal
          open={openDeleteModal}
          handelClose={() => setOpenDeleteModal(false)}
          onUpdate={() => handleDelete(deletedId)}
        />
      )}

      <>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {/* Search */}
          <Grid justifyContent="start" display="flex" item xs={12} sm={4} md={3}>
            <SearchComponet onSearch={setSearch} />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setPage(0);
                }}
                sx={{ paddingX: '10px', borderRadius: '10px' }}
                renderValue={(selected) => {
                  if (!selected)
                    return (
                      <Typography color="text.secondary">
                        {t('interface.category')}
                      </Typography>
                    );
                  const item = categoryOptions.find((o) => o.value === selected);
                  return (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography margin="0">
                        {isRtl ? item?.labelAr : item?.labelEn}
                      </Typography>
                    </Box>
                  );
                }}
                IconComponent={() => (
                  <DropdownIcon style={{ width: '15px', height: '15px' }} />
                )}
              >
                <MenuItem value="">
                  <Typography color="text.secondary">
                    {t('General.ALL')}
                  </Typography>
                </MenuItem>
                {categoryOptions.map((item, index) => (
                  <MenuItem key={index} value={item?.value}>
                    <Typography margin="0">
                      {isRtl ? item?.labelAr : item?.labelEn}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Add Button */}
          <Grid justifyContent="end" display="flex" item xs={12} sm={4} md={6}>
            <Button
              onClick={handleOpenAddModal}
              style={{
                backgroundColor: 'black',
                color: 'white',
                borderRadius: '10px',
                padding: '21px 24px',
                width: 'fit-content',
                height: '56px',
              }}
            >
              {' '}
              + {t('interface.addInterface')}
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Stack>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </Stack>
        ) : (
          <>
            <CityReqTable
              handleChangePage={handleChangePage}
              page={page}
              requests={clientReqList}
              categoryFilter={category}
              onDelete={(id) => {
                setDeletedId(id);
                setOpenDeleteModal(true);
              }}
              onEdit={(row) => {
                setEditData(row);
                setMode('edit');
                setModalOpen(true);
              }}
            />
          </>
        )}
      </>
    </>
  );
}
